import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { authenticateApp } from "./auth-context";
import { useAppState } from "./app-state";
import {
  DashboardDisplayState,
  DashboardState,
  LoadingState,
  ProcessedAppointment,
  ProcessedPrescription,
} from "../pages/DashboardNew/interface";
import UserDashboardData from "../pages/DashboardNew/dashboardHelper";
import { getNextUpcomingAppointments } from "../utils/eira/helper";

// Data Types for API calls
type DataType =
  | "UserDetails"
  | "Prakriti"
  | "Symtoms"
  | "BloodReport"
  | "TongueImage"
  | "HealthScore"
  | "DcotorsList"
  | "Appointments"
  | "Prescription"
  | "DailyScores"
  | "AyurvedaCorner"
  | "HealthHistory";

// Error state interface
interface ErrorState {
  [key: string]: string | null;
}

// Appointment status types
type AppointmentStatus = "none" | "scheduled" | "past";

// Dashboard Context Interface
interface IDashboardContext {
  // Data
  dashboardData: DashboardState;
  loadingState: LoadingState;
  errors: ErrorState;

  // Display logic state
  displayState: DashboardDisplayState;

  // Loading states (for backward compatibility + new ones)
  isLoadingPrescriptions: boolean;
  isLoadingAppointments: boolean;
  isLoadingDoctors: boolean;
  isLoadingUserDetails: boolean;
  isLoadingSymptoms: boolean;
  isLoadingHealthScore: boolean;
  isLoadingBloodReport: boolean;
  isLoadingDailyScores: boolean;
  isLoadingPrakriti: boolean;
  isLoadingTongueImage: boolean;
  isLoadingAyurvedaCorner: boolean;
  isLoadingHealthHistory: boolean;

  // Initial load flag
  hasInitiallyLoaded: boolean;

  // Single refresh function - main API
  refetchData: (dataTypes: DataType[]) => Promise<void>;

  // Initial load - NOW EXPOSED TO BE CALLED MANUALLY
  loadInitialData: () => Promise<void>;

  // Error management
  clearErrors: () => void;

  // Setters for individual loading states (for external components)
  setIsLoadingPrescriptions: (loading: boolean) => void;
  setIsLoadingAppointments: (loading: boolean) => void;
  setIsLoadingDoctors: (loading: boolean) => void;
  setIsLoadingHealthScore: (loading: boolean) => void;
  setIsLoadingBloodReport: (loading: boolean) => void;
  setIsLoadingDailyScores: (loading: boolean) => void;
  setIsLoadingPrakriti: (loading: boolean) => void;
  setIsLoadingTongueImage: (loading: boolean) => void;
  setIsLoadingAyurvedaCorner: (loading: boolean) => void;
  setIsLoadingHealthHistory: (loading: boolean) => void;

  // clear function for logout
  clearAllDashboardData: () => void;
  resetClearingState: () => void;
  isClearing: boolean;
}

// Create Context
export const dashboardContext = createContext<IDashboardContext | undefined>(
  undefined
);

// Dashboard Provider Hook
const useDashboardProvider = () => {
  const { clinicUserId: userId } = authenticateApp();
  const { currentClinic: client } = useAppState();

  // Track if initial data has been loaded
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState<boolean>(false);

  // Flag to prevent auto-loading during logout
  const [isClearing, setIsClearing] = useState<boolean>(false);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState<DashboardState>({
    userDetails: null,
    prakritiData: null,
    symptoms: [],
    bloodReports: [],
    tongueImages: [],
    healthScores: [],
    doctors: [],
    appointments: [],
    prescriptions: [],
    dailyScores: [],
    ayurvedicCorner: [],
    healthHistory: null,
  });

  // Loading states for each component
  const [loadingState, setLoadingState] = useState<LoadingState>({
    userDetails: false, // Changed from true
    prakritiData: false, // Changed from true
    symptoms: false, // Changed from true
    bloodReports: false, // Changed from true
    tongueImages: false, // Changed from true
    healthScores: false, // Changed from true
    doctors: false, // Changed from true
    appointments: false, // Changed from true
    prescriptions: false, // Changed from true
    dailyScores: false, // Changed from true
    ayurvedicCorner: false, // Changed from true
    healthHistory: false,
    initialLoad: false, // Changed from true
  });

  // Individual loading states for backward compatibility + new ones
  const [isLoadingPrescriptions, setIsLoadingPrescriptions] =
    useState<boolean>(false);
  const [isLoadingAppointments, setIsLoadingAppointments] =
    useState<boolean>(false);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState<boolean>(false);
  const [isLoadingUserDetails, setIsLoadingUserDetails] =
    useState<boolean>(false);
  const [isLoadingSymptoms, setIsLoadingSymptoms] = useState<boolean>(false);

  // New individual loading states for the requested data types
  const [isLoadingHealthScore, setIsLoadingHealthScore] =
    useState<boolean>(false);
  const [isLoadingBloodReport, setIsLoadingBloodReport] =
    useState<boolean>(false);
  const [isLoadingDailyScores, setIsLoadingDailyScores] =
    useState<boolean>(false);
  const [isLoadingPrakriti, setIsLoadingPrakriti] = useState<boolean>(false);
  const [isLoadingTongueImage, setIsLoadingTongueImage] =
    useState<boolean>(false);
  const [isLoadingAyurvedaCorner, setIsLoadingAyurvedaCorner] =
    useState<boolean>(false);
  const [isLoadingHealthHistory, setIsLoadingHealthHistory] = // Add Health History loading state
    useState<boolean>(false);

  // Error states
  const [errors, setErrors] = useState<ErrorState>({});

  // Display state for appointment/prescription/doctor logic
  const [displayState, setDisplayState] = useState<DashboardDisplayState>({
    nextUpcomingAppointments: [], // Changed from nextAppointment: null
    hasUpcomingAppointments: false,
    latestPrescription: null,
    hasPrescription: false,
    shouldShowRecommendedDoctors: true,
    shouldShowPrescription: false,
    shouldShowAppointments: false,
    displayOrder: ["doctors"], // Default to showing doctors only
  });

  // Create dashboard instance
  const dashboardInstance = useMemo(() => {
    if (userId && client) {
      return new UserDashboardData(userId, client);
    }
    return null;
  }, [userId, client]);

  // Helper function to map data types to loading state keys
  const getLoadingStateKey = useCallback(
    (dataType: DataType): keyof LoadingState | null => {
      const mapping: Record<DataType, keyof LoadingState> = {
        UserDetails: "userDetails",
        Prakriti: "prakritiData",
        Symtoms: "symptoms",
        BloodReport: "bloodReports",
        TongueImage: "tongueImages",
        HealthScore: "healthScores",
        DcotorsList: "doctors",
        Appointments: "appointments",
        Prescription: "prescriptions",
        DailyScores: "dailyScores",
        AyurvedaCorner: "ayurvedicCorner",
        HealthHistory: "healthHistory",
      };
      return mapping[dataType] || null;
    },
    []
  );

  // Helper function to update individual loading states
  const updateIndividualLoadingStates = useCallback(
    (dataTypes: DataType[], isLoading: boolean) => {
      dataTypes.forEach((dataType) => {
        switch (dataType) {
          case "Prescription":
            setIsLoadingPrescriptions(isLoading);
            break;
          case "Appointments":
            setIsLoadingAppointments(isLoading);
            break;
          case "DcotorsList":
            setIsLoadingDoctors(isLoading);
            break;
          case "UserDetails":
            setIsLoadingUserDetails(isLoading);
            break;
          case "Symtoms":
            setIsLoadingSymptoms(isLoading);
            break;
          case "HealthScore":
            setIsLoadingHealthScore(isLoading);
            break;
          case "BloodReport":
            setIsLoadingBloodReport(isLoading);
            break;
          case "DailyScores":
            setIsLoadingDailyScores(isLoading);
            break;
          case "Prakriti":
            setIsLoadingPrakriti(isLoading);
            break;
          case "TongueImage":
            setIsLoadingTongueImage(isLoading);
            break;
          case "AyurvedaCorner":
            setIsLoadingAyurvedaCorner(isLoading);
            break;
          case "HealthHistory": // Add Health History case
            setIsLoadingHealthHistory(isLoading);
            break;
          default:
            console.warn(`Unknown data type for loading state: ${dataType}`);
            break;
        }
      });
    },
    []
  );

  // Helper function to determine appointment status and display logic
  const calculateDisplayState = useCallback(
    (
      appointments: ProcessedAppointment[],
      prescriptions: ProcessedPrescription[]
    ): DashboardDisplayState => {
      // Get next upcoming appointments using the new logic
      const nextUpcomingAppointments =
        getNextUpcomingAppointments(appointments);
      const hasUpcomingAppointments = nextUpcomingAppointments.length > 0;

      // Get latest prescription (prescriptions are already sorted by createdAt desc)
      const latestPrescription =
        prescriptions.length > 0 ? prescriptions[0] : null;
      const hasPrescription = latestPrescription !== null;

      // Apply the new business logic
      let shouldShowRecommendedDoctors = false;
      let shouldShowPrescription = false;
      let shouldShowAppointments = false;
      let displayOrder: ("prescription" | "appointments" | "doctors")[] = [];

      if (hasUpcomingAppointments) {
        // Case 1: Has upcoming appointments
        shouldShowAppointments = true;

        if (hasPrescription) {
          // Case 1a: Has upcoming appointment AND prescription
          // Show: Prescription first, then appointment
          shouldShowPrescription = true;
          shouldShowRecommendedDoctors = false;
          displayOrder = ["prescription", "appointments"];
        } else {
          // Case 1b: Has upcoming appointment but NO prescription
          // Show: Only appointment
          shouldShowPrescription = false;
          shouldShowRecommendedDoctors = false;
          displayOrder = ["appointments"];
        }
      } else {
        // Case 2: No upcoming appointments
        shouldShowAppointments = false;

        if (hasPrescription) {
          // Case 2a: No upcoming appointment but HAS prescription
          // Show: Prescription first, then recommended doctors
          shouldShowPrescription = true;
          shouldShowRecommendedDoctors = true;
          displayOrder = ["prescription", "doctors"];
        } else {
          // Case 2b: No upcoming appointment and NO prescription
          // Show: Only recommended doctors
          shouldShowPrescription = false;
          shouldShowRecommendedDoctors = true;
          displayOrder = ["doctors"];
        }
      }

      return {
        nextUpcomingAppointments,
        hasUpcomingAppointments,
        latestPrescription,
        hasPrescription,
        shouldShowRecommendedDoctors,
        shouldShowPrescription,
        shouldShowAppointments,
        displayOrder,
      };
    },
    []
  );

  // Update display state whenever appointments or prescriptions change
  useEffect(() => {
    const newDisplayState = calculateDisplayState(
      dashboardData.appointments,
      dashboardData.prescriptions
    );
    setDisplayState(newDisplayState);
  }, [
    dashboardData.appointments,
    dashboardData.prescriptions,
    calculateDisplayState,
  ]);

  // Clear errors function
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Main refetch function - single API for refreshing any component data
  const refetchData = useCallback(
    async (dataTypes: DataType[]) => {
      if (!dashboardInstance) {
        console.warn("Dashboard instance not available");
        return;
      }

      try {
        // Set loading states for requested data
        const newLoadingState = { ...loadingState };
        dataTypes.forEach((type) => {
          const key = getLoadingStateKey(type);
          if (key) newLoadingState[key] = true;
        });
        setLoadingState(newLoadingState);

        // Update individual loading states
        updateIndividualLoadingStates(dataTypes, true);

        // Fetch latest data for specified components
        const latestData = await dashboardInstance.getLatestDashboardData(
          dataTypes,
          true
        );

        // Update only the requested data types, keeping existing data for others
        setDashboardData((prev: DashboardState) => {
          const updatedData = { ...prev };

          // Only update the specific data types that were requested
          dataTypes.forEach((dataType) => {
            switch (dataType) {
              case "UserDetails":
                if (latestData.userDetails !== undefined) {
                  updatedData.userDetails = latestData.userDetails;
                }
                break;
              case "Prakriti":
                if (latestData.prakritiData !== undefined) {
                  updatedData.prakritiData = latestData.prakritiData;
                }
                break;
              case "Symtoms":
                if (latestData.symptoms !== undefined) {
                  updatedData.symptoms = latestData.symptoms;
                }
                break;
              case "BloodReport":
                if (latestData.bloodReports !== undefined) {
                  updatedData.bloodReports = latestData.bloodReports;
                }
                break;
              case "TongueImage":
                if (latestData.tongueImages !== undefined) {
                  updatedData.tongueImages = latestData.tongueImages;
                }
                break;
              case "HealthScore":
                if (latestData.healthScores !== undefined) {
                  updatedData.healthScores = latestData.healthScores;
                }
                break;
              case "DcotorsList":
                if (latestData.doctors !== undefined) {
                  updatedData.doctors = latestData.doctors;
                }
                break;
              case "Appointments":
                if (latestData.appointments !== undefined) {
                  updatedData.appointments = latestData.appointments;
                }
                break;
              case "Prescription":
                if (latestData.prescriptions !== undefined) {
                  updatedData.prescriptions = latestData.prescriptions;
                }
                break;
              case "DailyScores":
                if (latestData.dailyScores !== undefined) {
                  updatedData.dailyScores = latestData.dailyScores;
                }
                break;
              case "AyurvedaCorner":
                if (latestData.ayurvedicCorner !== undefined) {
                  updatedData.ayurvedicCorner = latestData.ayurvedicCorner;
                }
                break;
              case "HealthHistory": // Add Health History case
                if (latestData.healthHistory !== undefined) {
                  updatedData.healthHistory = latestData.healthHistory;
                }
                break;
              default:
                console.warn(`Unknown data type for update: ${dataType}`);
                break;
            }
          });

          return updatedData;
        });

        // Update loading states
        const updatedLoadingState = { ...newLoadingState };
        dataTypes.forEach((type) => {
          const key = getLoadingStateKey(type);
          if (key) updatedLoadingState[key] = false;
        });
        setLoadingState(updatedLoadingState);

        // Update individual loading states
        updateIndividualLoadingStates(dataTypes, false);

        // Clear errors for successfully refreshed data
        const newErrors = { ...errors };
        dataTypes.forEach((type) => {
          delete newErrors[type];
        });
        setErrors(newErrors);

        console.log(
          "✅ Data refetched successfully for:",
          dataTypes,
          latestData
        );
      } catch (error) {
        console.error("❌ Error refetching data:", error);

        // Set error for failed refresh
        const errorMessage =
          error instanceof Error ? error.message : "Failed to refetch data";
        setErrors((prev: ErrorState) => ({
          ...prev,
          refetch: errorMessage,
        }));

        // Update loading states even on error
        const updatedLoadingState = { ...loadingState };
        dataTypes.forEach((type) => {
          const key = getLoadingStateKey(type);
          if (key) updatedLoadingState[key] = false;
        });
        setLoadingState(updatedLoadingState);

        // Update individual loading states
        updateIndividualLoadingStates(dataTypes, false);
      }
    },
    [
      dashboardInstance,
      loadingState,
      getLoadingStateKey,
      updateIndividualLoadingStates,
      errors,
    ]
  );

  // Initial data load - NOW TO BE CALLED MANUALLY
  const loadInitialData = useCallback(async () => {
    if (!dashboardInstance) {
      console.warn("Dashboard instance not available");
      return;
    }

    // 🔥 Don't load if we're in clearing state (logout in progress)
    if (isClearing) {
      return;
    }

    // Don't load again if already loaded
    if (hasInitiallyLoaded) {
      return;
    }

    try {
      // Set initial loading state
      setLoadingState((prev: LoadingState) => ({ ...prev, initialLoad: true }));

      // Update individual loading states
      setIsLoadingPrescriptions(true);
      setIsLoadingAppointments(true);
      setIsLoadingDoctors(true);
      setIsLoadingUserDetails(true);
      setIsLoadingSymptoms(true);
      setIsLoadingHealthScore(true);
      setIsLoadingBloodReport(true);
      setIsLoadingDailyScores(true);
      setIsLoadingPrakriti(true);
      setIsLoadingTongueImage(true);
      setIsLoadingAyurvedaCorner(true);

      const allData = await dashboardInstance.getAllDashboardData();

      // Update state with all data at once
      setDashboardData(allData);

      // Set all loading states to false
      setLoadingState({
        userDetails: false,
        prakritiData: false,
        symptoms: false,
        bloodReports: false,
        tongueImages: false,
        healthScores: false,
        doctors: false,
        appointments: false,
        prescriptions: false,
        dailyScores: false,
        ayurvedicCorner: false,
        healthHistory: false,
        initialLoad: false,
      });

      // Update individual loading states
      setIsLoadingPrescriptions(false);
      setIsLoadingAppointments(false);
      setIsLoadingDoctors(false);
      setIsLoadingUserDetails(false);
      setIsLoadingSymptoms(false);
      setIsLoadingHealthScore(false);
      setIsLoadingBloodReport(false);
      setIsLoadingDailyScores(false);
      setIsLoadingPrakriti(false);
      setIsLoadingTongueImage(false);
      setIsLoadingHealthHistory(false);
      setIsLoadingAyurvedaCorner(false);

      // Mark as initially loaded
      setHasInitiallyLoaded(true);

      // Clear any previous errors
      setErrors({});
    } catch (error) {
      console.error("❌ Error loading initial dashboard data:", error);
      setErrors({ general: "Failed to load dashboard data" });

      // Set loading states to false even on error
      setLoadingState((prev: LoadingState) => ({
        ...prev,
        initialLoad: false,
      }));
      setIsLoadingPrescriptions(false);
      setIsLoadingAppointments(false);
      setIsLoadingDoctors(false);
      setIsLoadingUserDetails(false);
      setIsLoadingSymptoms(false);
      setIsLoadingHealthScore(false);
      setIsLoadingBloodReport(false);
      setIsLoadingDailyScores(false);
      setIsLoadingPrakriti(false);
      setIsLoadingTongueImage(false);
      setIsLoadingAyurvedaCorner(false);
      setIsLoadingHealthHistory(false);
    }
  }, [dashboardInstance, hasInitiallyLoaded, isClearing]); // 🔥 Add isClearing to dependencies

  // REMOVED AUTOMATIC useEffect - No longer automatically loads data on mount

  // Clear all dashboard data function
  const clearAllDashboardData = () => {
    // 🔥 Set clearing flag to prevent auto-loading
    setIsClearing(true);

    setDashboardData({
      userDetails: null,
      prakritiData: null,
      symptoms: [],
      bloodReports: [],
      tongueImages: [],
      healthScores: [],
      doctors: [],
      appointments: [],
      prescriptions: [],
      dailyScores: [],
      healthHistory: null,
      ayurvedicCorner: [],
    });

    setLoadingState({
      userDetails: false,
      prakritiData: false,
      symptoms: false,
      bloodReports: false,
      tongueImages: false,
      healthScores: false,
      doctors: false,
      appointments: false,
      prescriptions: false,
      dailyScores: false,
      ayurvedicCorner: false,
      healthHistory: false,
      initialLoad: false,
    });
    setDisplayState({
      nextUpcomingAppointments: [], // Changed from nextAppointment: null
      hasUpcomingAppointments: false,
      latestPrescription: null,
      hasPrescription: false,
      shouldShowRecommendedDoctors: true,
      shouldShowPrescription: false,
      shouldShowAppointments: false,
      displayOrder: ["doctors"], // Default to showing doctors only
    });
    // 🔥 Set hasInitiallyLoaded to false but keep isClearing true
    setHasInitiallyLoaded(false);
    setErrors({});

    // Reset all individual loading states
    setIsLoadingPrescriptions(false);
    setIsLoadingAppointments(false);
    setIsLoadingDoctors(false);
    setIsLoadingUserDetails(false);
    setIsLoadingSymptoms(false);
    setIsLoadingHealthScore(false);
    setIsLoadingBloodReport(false);
    setIsLoadingDailyScores(false);
    setIsLoadingPrakriti(false);
    setIsLoadingTongueImage(false);
    setIsLoadingHealthHistory(false);
    setIsLoadingAyurvedaCorner(false);
  };

  // Function to reset clearing flag when new user logs in
  const resetClearingState = () => {
    setIsClearing(false);
  };

  return {
    // Data
    dashboardData,
    loadingState,
    errors,

    // Display logic state
    displayState,

    // Initial load flag
    hasInitiallyLoaded,

    // Individual loading states for backward compatibility + new ones
    isLoadingPrescriptions,
    isLoadingAppointments,
    isLoadingDoctors,
    isLoadingUserDetails,
    isLoadingSymptoms,
    isLoadingHealthScore,
    isLoadingBloodReport,
    isLoadingDailyScores,
    isLoadingPrakriti,
    isLoadingTongueImage,
    isLoadingHealthHistory,
    isLoadingAyurvedaCorner,

    // Main API - single refetch function
    refetchData,

    // Initial load - NOW EXPOSED TO BE CALLED MANUALLY
    loadInitialData,

    // Error management
    clearErrors,

    // Setters
    setIsLoadingPrescriptions,
    setIsLoadingAppointments,
    setIsLoadingDoctors,
    setIsLoadingHealthScore,
    setIsLoadingBloodReport,
    setIsLoadingDailyScores,
    setIsLoadingPrakriti,
    setIsLoadingTongueImage,
    setIsLoadingHealthHistory,
    setIsLoadingAyurvedaCorner,
    clearAllDashboardData,
    resetClearingState,
    isClearing,
  };
};

// Provider Component
interface Props {
  children: React.ReactNode;
}

export const DashboardProvider = ({ children }: Props) => {
  const dashboardState = useDashboardProvider();

  return (
    <dashboardContext.Provider value={dashboardState}>
      {children}
    </dashboardContext.Provider>
  );
};

// Hook to use dashboard context
export const useDashboardState = () => {
  const context = useContext(dashboardContext);
  if (!context) {
    throw new Error(
      "useDashboardState must be used within a DashboardProvider"
    );
  }
  return context;
};

// Keep the old export for backward compatibility
export const dashboardState = useDashboardState;
